import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined, PlusOutlined, StopOutlined, } from "@ant-design/icons";
import { Button, message, Modal, Space, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
// Components
import { Filters } from "@/components/filters";
// Hooks
import { getRootStore } from "@/stores/RootStore";
// Types and Utils
import { RouteStatus } from "@/types/route/route.common";
import { getRouteStatusColor, ROUTE_MESSAGES } from "@/utils/routeHelpers";
// Styles
import { DataTable } from "@/components/DataTable";
import styles from "@/styles/RoutesPage.module.less";
import { ROUTE_STATUS_LABELS } from "@/types/route/route.common";
import { formatDateAndTime } from "@/utils/dateAndTimeFormat";
/**
 * RoutesPage Component
 * Following SOLID principles and project page structure patterns:
 * - Single Responsibility: Only handles page layout and orchestration
 * - Uses established Page - Store - Service architecture
 * - Follows the same structure as RefactoredOrdersPage
 */
const RoutesPage = observer(() => {
    const { routeListStore, singleRouteStore2 } = getRootStore();
    const navigate = useNavigate();
    useEffect(() => {
        routeListStore.dataTableStore.setFilters(routeListStore.filtersStore.values);
        routeListStore.dataTableStore.loadData();
    }, [routeListStore.filtersStore.values]);
    // === EVENT HANDLERS ===
    const handleRouteOpen = (routeId) => {
        if (routeId) {
            navigate(`/routes/${routeId}`);
        }
        else {
            navigate("/routes/new");
        }
    };
    const handleDelete = (routeId) => {
        Modal.confirm({
            title: "Подтверждение удаления",
            content: ROUTE_MESSAGES.CONFIRM.DELETE,
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            onOk: async () => {
                try {
                    await singleRouteStore2.deleteRoute(routeId);
                    await routeListStore.refreshRoutes();
                    message.success(ROUTE_MESSAGES.SUCCESS.DELETE);
                }
                catch (error) {
                    message.error(ROUTE_MESSAGES.ERROR.DELETE);
                }
            },
        });
    };
    const handleCancel = (routeId) => {
        Modal.confirm({
            title: "Отменить маршрут?",
            content: ROUTE_MESSAGES.CONFIRM.CANCEL,
            okText: "Да, отменить",
            okType: "danger",
            cancelText: "Нет",
            onOk: async () => {
                try {
                    await singleRouteStore2.cancelRouteById(routeId);
                    await routeListStore.refreshRoutes();
                    message.success(ROUTE_MESSAGES.SUCCESS.CANCEL);
                }
                catch (error) {
                    message.error(ROUTE_MESSAGES.ERROR.CANCEL);
                }
            },
        });
    };
    // === TABLE CONFIGURATION ===
    const columns = [
        {
            title: "ID маршрута",
            dataIndex: "routeId",
            key: "routeId",
            width: 150,
            fixed: "left",
            render: (id) => (_jsx(Button, { type: "link", className: styles.routeNumber, onClick: () => handleRouteOpen(id), style: { padding: 0, height: "auto" }, children: id.toUpperCase() })),
        },
        {
            title: "Статус",
            dataIndex: "routeStatus",
            key: "routeStatus",
            width: 120,
            render: (status) => (_jsx(Tag, { color: getRouteStatusColor(status), children: ROUTE_STATUS_LABELS[status] })),
        },
        {
            title: "Техник",
            key: "technicianFullName",
            width: 180,
            render: (_, record) => {
                if (record.technicianFullName) {
                    return record.technicianFullName;
                }
                return _jsx("span", { className: styles.notAssigned, children: "\u041D\u0435 \u043D\u0430\u0437\u043D\u0430\u0447\u0435\u043D" });
            },
        },
        {
            title: "Начало",
            key: "startTimes",
            width: 180,
            render: (_, record) => (_jsxs("div", { className: styles.dateCell, children: [_jsx(Tooltip, { title: "\u041F\u043B\u0430\u043D\u043E\u0432\u0430\u044F \u0434\u0430\u0442\u0430", children: _jsxs("div", { className: styles.dateLine, children: [_jsx(CalendarOutlined, {}), _jsx("span", { children: record.plannedStartTime
                                        ? formatDateAndTime(record.plannedStartTime)
                                        : _jsx("span", { className: styles.dateEmpty, children: "\u2014" }) })] }) }), _jsx(Tooltip, { title: "\u041E\u0436\u0438\u0434\u0430\u0435\u043C\u0430\u044F \u0434\u0430\u0442\u0430", children: _jsxs("div", { className: styles.dateLine, children: [_jsx(ClockCircleOutlined, {}), _jsx("span", { children: record.expectedStartTime
                                        ? formatDateAndTime(record.expectedStartTime)
                                        : _jsx("span", { className: styles.dateEmpty, children: "\u2014" }) })] }) }), _jsx(Tooltip, { title: "\u0424\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u0434\u0430\u0442\u0430", children: _jsxs("div", { className: styles.dateLine, children: [_jsx(CheckCircleOutlined, {}), _jsx("span", { children: record.actualStartTime
                                        ? formatDateAndTime(record.actualStartTime)
                                        : _jsx("span", { className: styles.dateEmpty, children: "\u2014" }) })] }) })] })),
            sorter: (a, b) => dayjs(a.plannedStartTime || 0).unix() - dayjs(b.plannedStartTime || 0).unix(),
        },
        {
            title: "Окончание",
            key: "endTimes",
            width: 180,
            render: (_, record) => (_jsxs("div", { className: styles.dateCell, children: [_jsx(Tooltip, { title: "\u041F\u043B\u0430\u043D\u043E\u0432\u0430\u044F \u0434\u0430\u0442\u0430", children: _jsxs("div", { className: styles.dateLine, children: [_jsx(CalendarOutlined, {}), _jsx("span", { children: record.plannedEndTime
                                        ? formatDateAndTime(record.plannedEndTime)
                                        : _jsx("span", { className: styles.dateEmpty, children: "\u2014" }) })] }) }), _jsx(Tooltip, { title: "\u041E\u0436\u0438\u0434\u0430\u0435\u043C\u0430\u044F \u0434\u0430\u0442\u0430", children: _jsxs("div", { className: styles.dateLine, children: [_jsx(ClockCircleOutlined, {}), _jsx("span", { children: record.expectedEndTime
                                        ? formatDateAndTime(record.expectedEndTime)
                                        : _jsx("span", { className: styles.dateEmpty, children: "\u2014" }) })] }) }), _jsx(Tooltip, { title: "\u0424\u0430\u043A\u0442\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u0434\u0430\u0442\u0430", children: _jsxs("div", { className: styles.dateLine, children: [_jsx(CheckCircleOutlined, {}), _jsx("span", { children: record.actualEndTime
                                        ? formatDateAndTime(record.actualEndTime)
                                        : _jsx("span", { className: styles.dateEmpty, children: "\u2014" }) })] }) })] })),
            sorter: (a, b) => dayjs(a.plannedEndTime || 0).unix() - dayjs(b.plannedEndTime || 0).unix(),
        },
        {
            title: "Номера заказов",
            key: "orderNumbers",
            width: 200,
            render: (_, record) => {
                const orders = record.orderNumbers;
                if (!orders || orders.length === 0)
                    return _jsx("span", { className: styles.notAssigned, children: "\u2014" });
                return (_jsx(Space, { size: [4, 4], wrap: true, children: orders.map((ref) => (_jsx(Tag, { color: "blue", style: { cursor: "pointer", margin: 0 }, onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/orders/${ref.orderId}`);
                        }, children: ref.orderNumber }, ref.orderId))) }));
            },
        },
        {
            title: "Действия",
            key: "actions",
            fixed: "right",
            width: 140,
            render: (_, record) => {
                const isCreated = record.routeStatus === RouteStatus.CREATED;
                const isPlanned = record.routeStatus === RouteStatus.PLANNED;
                if (!isCreated && !isPlanned)
                    return null;
                return (_jsxs(Space, { size: "small", children: [isCreated && (_jsx(Tooltip, { title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C", children: _jsx(Button, { type: "text", icon: _jsx(DeleteOutlined, {}), onClick: () => handleDelete(record.routeId), size: "small", danger: true }) })), isPlanned && (_jsx(Tooltip, { title: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442", children: _jsx(Button, { type: "text", icon: _jsx(StopOutlined, {}), onClick: () => handleCancel(record.routeId), size: "small", danger: true }) }))] }));
            },
        },
    ];
    // === RENDER ===
    return (_jsx(DataTable, { store: routeListStore.dataTableStore, config: {
            header: {
                title: "Управление маршрутами",
                actions: (_jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: () => handleRouteOpen(), children: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u043C\u0430\u0440\u0448\u0440\u0443\u0442" })),
                extraContent: (_jsx(Filters, { filtersStore: routeListStore.filtersStore, title: "\u0424\u0438\u043B\u044C\u0442\u0440\u044B \u0438 \u043F\u043E\u0438\u0441\u043A", showClearAll: true, showApplyButton: false })),
            },
            columns: columns,
            rowKey: "routeId",
            scroll: { x: 1400 },
        } }));
});
RoutesPage.displayName = "RoutesPage";
export default RoutesPage;
